import type { ReactNode } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import { Box, HStack } from '@enechain/ecloud-designsystem'

import {
  IssuerAreaStatus,
  IssuerSelectorView,
} from '~/components/bidding/common/IssuerSelector'
import { buildSelectedAreasLabel } from '~/components/bidding/common/areaLabel'
import type { IssuerOption } from '~/components/bidding/common/bidIssuer'
import { issuerTypeLabel } from '~/components/bidding/common/bidIssuer'
import { WEEKDAY_KEYS } from '~/components/common/DayGrid'
import { TradeSideRadioGroup } from '~/components/common/TradeSideRadioGroup'
import {
  CollapsibleFilterToolbar,
  FilterGrid,
  type FilterGridItem,
  type ToolbarStatusItem,
} from '~/components/common/filters'
import { JEPX_AREA_CODES } from '~/models/area'
import { formatDateDisplay } from '~/models/dateTime'
import { IntradayAreaMultiSelect } from '~/pages/IntradayBidding/components/IntradayAreaMultiSelect'
import type { IntradaySideFilter } from '~/pages/IntradayBidding/models/globalFilters'

/** 4 タブ共通の開閉の保存キー。 タブを切り替えても開閉が変わらない。 */
export const INTRADAY_FILTER_STORAGE_KEY = 'intradayBidding'

type Props = {
  /**
   * 受渡日。 `control` は条件のカードの先頭に置く部品 (過去日の可否はタブごとに呼び出し側が決める)、
   * `date` は閉じた時の要約に出す値。
   */
  deliveryDate: { date: Date; control: ReactNode }
  /**
   * 発注主体を出すか (入札追加・板発注。 AC-BIN-131)。 選択肢が 0 件の間は出さない。
   * 既定の区分の選択と選択肢に無い区分の取り消しは画面 (`useIssuerSelection`) が持つ。 カードは閉じている間
   * 描かないので、 ここでは表示だけ (IssuerSelectorView) にする (#12102 レビュー)。
   */
  issuer?: {
    value: Parameters<typeof IssuerSelectorView>[0]['value']
    onChange: Parameters<typeof IssuerSelectorView>[0]['onChange']
    options: IssuerOption[]
    selected: IssuerOption | undefined
  }
  /** 全体フィルターのエリア・売/買を出すか (板発注を除く。 AC-BIN-103 / AC-BIN-131)。 */
  global?: {
    selectedAreas: Set<AreaCode>
    onAreasChange: (next: Set<AreaCode>) => void
    side: IntradaySideFilter
    onSideChange: (next: IntradaySideFilter) => void
  }
}

/** カードと要約の並び。 受渡日 → 発注主体区分 → エリア → 売/買。 */
const ITEM_ORDER = ['deliveryDate', 'issuer', 'area', 'side']

const byItemOrder = (a: { key: string }, b: { key: string }): number =>
  ITEM_ORDER.indexOf(a.key) - ITEM_ORDER.indexOf(b.key)

/** エリアの要約。 全エリアは「すべて」 (空白にしない)。 */
export const areaSummaryLabel = (
  selectedAreas: Set<AreaCode>,
  allLabel: string,
): string =>
  JEPX_AREA_CODES.every((code) => selectedAreas.has(code))
    ? allLabel
    : buildSelectedAreasLabel(selectedAreas)

/**
 * 時間前入札の全体フィルターの開閉式バー (画面の最上部・ページタブの上。 4 タブ共通の 1 本)。
 * UI 辞書 toolbar-collapsible.md。
 *
 * 全体フィルターのグループだけを持つ。 タブ固有の絞り込み (入札一覧・板発注) は各タブの中の
 * 別の開閉式カードで、 ここには混ぜない。
 *
 * - 選んだり変えたりする部品はすべて条件のカードに置き、 閉じている間は要約で今の値を読む
 *   (2026-10-26 デザイナー「受渡日の選択もフィルターの card 内に表示すべき」)。 ツールバーに
 *   残す部品 (`leading`) は持たない
 * - カード = 受渡日 → 発注主体区分 (+ 選べるエリアの文) → エリア → 売/買
 *   (板発注はエリア・売/買なし)。 スポット市場と同じく、 エリアは発注主体区分の右に置く。
 *   順は `pinAreaAndPeriodFirst` に任せず [[ITEM_ORDER]] で決める
 * - 要約 = カードと同じ順。 既定も「すべて」 と出す
 * - 件数・クリア = エリアと売/買だけ。 受渡日・発注主体は数えず、 戻さない
 * - GC カウントダウンはこのバーに含めない (AC-BIN-131 ※。 ページヘッダー右端)
 */
export const IntradayFilterToolbar = ({
  deliveryDate,
  issuer,
  global,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const all = t('common:filter.all')
  const deliveryDateLabel = t('page:biddingIntraday.market.deliveryDate')
  const items: FilterGridItem[] = [
    {
      key: 'deliveryDate',
      label: deliveryDateLabel,
      control: deliveryDate.control,
    },
  ]
  // 要約の日付は日付欄 (CompactCivilDateField) の閉じた表示と同じ `YYYY/MM/DD (曜)`
  const summary: ToolbarStatusItem[] = [
    {
      key: 'deliveryDate',
      label: deliveryDateLabel,
      value: t('common:date.withWeekday', {
        date: formatDateDisplay(deliveryDate.date),
        weekday: t(
          `common:date.calendar.weekday.${WEEKDAY_KEYS[deliveryDate.date.getDay()]}`,
        ),
      }),
    },
  ]

  if (issuer !== undefined && issuer.options.length > 0) {
    const issuerLabel = t('page:biddingIntraday.detail.issuer')
    items.push({
      key: 'issuer',
      label: issuerLabel,
      control: (
        <HStack alignItems="center" flexWrap="wrap" gap={4}>
          <Box width="280px" maxWidth="100%">
            <IssuerSelectorView
              hideAreaStatus
              hideLabel
              label={issuerLabel}
              onChange={issuer.onChange}
              options={issuer.options}
              value={issuer.value}
            />
          </Box>
          <IssuerAreaStatus marginTop={0} selected={issuer.selected} />
        </HStack>
      ),
    })
    if (issuer.selected !== undefined) {
      summary.push({
        key: 'issuer',
        label: issuerLabel,
        value: issuerTypeLabel(issuer.selected.issuerType),
      })
    }
  }

  if (global !== undefined) {
    const areaLabel = t('page:biddingIntraday.area.label')
    const sideLabel = t('page:biddingIntraday.history.sideLabel')
    items.push(
      {
        key: 'area',
        label: areaLabel,
        control: (
          <IntradayAreaMultiSelect
            onAreasChange={global.onAreasChange}
            selectedAreas={global.selectedAreas}
          />
        ),
      },
      {
        key: 'side',
        label: sideLabel,
        // 買 / 売 は一覧の行バッジと同じ pill で示す (design/bidding/intraday.html の 2026-07-28 統一)
        control: (
          <TradeSideRadioGroup
            allLabel={t('page:biddingIntraday.history.sideAll')}
            ariaLabel={sideLabel}
            name="intraday-history-dir"
            onChange={global.onSideChange}
            value={global.side}
            values={{ all: 'all', buy: 'buy', sell: 'sell' }}
          />
        ),
      },
    )
    summary.push(
      {
        key: 'area',
        label: areaLabel,
        value: areaSummaryLabel(global.selectedAreas, all),
      },
      {
        key: 'side',
        label: sideLabel,
        value:
          global.side === 'buy'
            ? t('common:tradeSide.buy')
            : global.side === 'sell'
              ? t('common:tradeSide.sell')
              : all,
      },
    )
  }

  // 件数・クリアの対象はエリアと売/買だけ (既定 = すべて)。 受渡日・発注主体は数えず、 戻さない。
  // 板発注はエリア・売/買を持たない (AC-BIN-103) ので 0 で、 クリアは押せない状態になる
  const modifiedCount =
    global === undefined
      ? 0
      : (JEPX_AREA_CODES.every((code) => global.selectedAreas.has(code))
          ? 0
          : 1) + (global.side === 'all' ? 0 : 1)
  const onClear =
    global === undefined
      ? undefined
      : (): void => {
          global.onAreasChange(new Set(JEPX_AREA_CODES))
          global.onSideChange('all')
        }

  return (
    <CollapsibleFilterToolbar
      keepSummaryOrder
      modifiedCount={modifiedCount}
      onClear={onClear}
      storageKey={INTRADAY_FILTER_STORAGE_KEY}
      summary={summary.sort(byItemOrder)}
    >
      <FilterGrid items={items.sort(byItemOrder)} keepOrder />
    </CollapsibleFilterToolbar>
  )
}
